// SPDX-License-Identifier: AGPL-3.0-or-later
import * as SnowflakeUtils from '@konvo/snowflake/src/SnowflakeUtils';
import {makeAutoObservable} from 'mobx';

/** Embedded assistant views participate in the same read state as the DM. */
class AssistantView {
	private channels = new Map<string, number>();
	/** Konvo panel: the request whose replies it shows in place, per DM channel. */
	private answering = new Map<string, string>();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	enter(channelId: string): () => void {
		this.channels.set(channelId, (this.channels.get(channelId) ?? 0) + 1);
		return () => this.leave(channelId);
	}

	private leave(channelId: string): void {
		const count = this.channels.get(channelId) ?? 0;
		if (count <= 1) this.channels.delete(channelId);
		else this.channels.set(channelId, count - 1);
	}

	has(channelId: string): boolean {
		return this.channels.has(channelId);
	}

	/** The panel shows the replies to `requestId` (null: none), so they need no notification. */
	setAnswering(channelId: string, requestId: string | null): void {
		if (requestId) this.answering.set(channelId, requestId);
		else this.answering.delete(channelId);
	}

	/** A message the panel shows as it arrives: sent after the request it is answering. */
	answers(channelId: string, messageId: string): boolean {
		const requestId = this.answering.get(channelId);
		return requestId != null && SnowflakeUtils.compare(messageId, requestId) > 0;
	}
}

export default new AssistantView();
